import { createStyles, Loader, Stack } from "@mantine/core";
import { Alert, Button, Modal, ModalContent, Select, tokens } from "@blurb/codex-react";
import { Flex, IconV3 } from "@rpi/openapi-core";
import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { RpaNumberStepperInput } from "../inputFields/RpaNumberStepperInput";
import { Body } from "../Typography";
import { ProductAttributes, ProductAttributesSchema } from "../../hooks/useStandardProducts";
import { clampPageCount, getPageCountBounds, PageCountBounds } from "../../utils/productSpecification.util";
import { shouldRetry } from "../../utils/query.util";
import {
  composeProductDescription,
  DownloadScope,
  requestTemplateDownloads,
  templateApiBaseUrl,
  TemplateFormat,
} from "../../utils/templateApi.util";
import { composeProductSpecLabel } from "../../views/customer-products/templateProductLabel";

export type DownloadTemplateModalProps = {
  opened: boolean;
  openapiProductId: number | undefined;
  onClose: () => void;
};

const FORMAT_OPTIONS: { value: TemplateFormat; label: string }[] = [
  { value: "pdf", label: "PDF" },
  { value: "png", label: "PNG" },
  { value: "idml", label: "IDML" },
];

const SCOPE_OPTIONS: { value: DownloadScope; label: string }[] = [
  { value: "cover-and-pages", label: "Cover and pages" },
  { value: "cover", label: "Cover only" },
  { value: "pages", label: "Pages only" },
];

const useStyles = createStyles({
  buttons: {
    marginTop: "var(--codex-spacing-2)",
  },
  loader: {
    padding: "var(--codex-spacing-6) 0",
  },
  errorContent: {
    display: "flex",
    alignItems: "center",
    gap: "var(--codex-spacing-2)",
  },
});

// Saves the fetched blob to disk via an anchor with a download attribute.
function saveBlob(blob: Blob, filename: string) {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

type DownloadTemplateFormProps = {
  manufacturingSku: string;
  attributes: ProductAttributes;
  bounds: PageCountBounds;
  onClose: () => void;
};

// Rendered only once the product has loaded, so the page-count state initialises from the real bounds.
function DownloadTemplateForm({ manufacturingSku, attributes, bounds, onClose }: DownloadTemplateFormProps) {
  const { classes } = useStyles();

  const [pageCount, setPageCount] = React.useState(bounds.min);
  const [format, setFormat] = React.useState<TemplateFormat | undefined>();
  const [scope, setScope] = React.useState<DownloadScope>("cover-and-pages");
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [formatError, setFormatError] = React.useState<string | undefined>();
  const [downloadFailed, setDownloadFailed] = React.useState(false);

  const handleDownload = async () => {
    if (!format) {
      setFormatError("Please select a format");
      return;
    }
    const normalizedPageCount = clampPageCount(pageCount, bounds);
    setPageCount(normalizedPageCount);
    setDownloadFailed(false);
    setIsDownloading(true);
    try {
      const { failed } = await requestTemplateDownloads({
        base: templateApiBaseUrl() ?? "",
        manufacturingSku,
        format,
        scope,
        pageCount: normalizedPageCount,
        productDescription: (artifact) =>
          composeProductDescription(composeProductSpecLabel(artifact, attributes), normalizedPageCount),
        triggerDownload: saveBlob,
      });
      if (failed === 0) onClose();
      else setDownloadFailed(true);
    } catch {
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack spacing={16}>
      <RpaNumberStepperInput
        label="Page Count"
        description={`Please choose a number between ${bounds.min} and ${bounds.max}, using increments of ${bounds.step}.`}
        min={bounds.min}
        max={bounds.max}
        step={bounds.step}
        value={pageCount}
        onChange={(value) => typeof value === "number" && setPageCount(value)}
        hideControls
        fullWidth
      />

      <Select
        label="Format"
        placeholder="Select..."
        options={FORMAT_OPTIONS}
        value={FORMAT_OPTIONS.find((option) => option.value === format) ?? null}
        error={formatError}
        onValueChange={(option) => {
          const next = FORMAT_OPTIONS.find((o) => o.value === option?.value)?.value;
          setFormat(next);
          if (next) setFormatError(undefined);
        }}
      />

      <Select
        label="Download"
        options={SCOPE_OPTIONS}
        value={SCOPE_OPTIONS.find((option) => option.value === scope) ?? null}
        onValueChange={(option) => {
          const next = SCOPE_OPTIONS.find((o) => o.value === option?.value)?.value;
          if (next) setScope(next);
        }}
      />

      {downloadFailed && (
        <Alert type="error" size="large">
          <div className={classes.errorContent}>
            <IconV3 icon="error" size={20} color={tokens.color.semantic.icon.danger} />
            <Body size="sm">Something went wrong. Please try again later.</Body>
          </div>
        </Alert>
      )}

      <Flex.Row gap={16} justify="flex-end" className={classes.buttons}>
        <Button variant="outlined" intent="neutral" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="filled"
          intent="primary"
          disabled={isDownloading}
          loading={isDownloading}
          onClick={handleDownload}
        >
          Download
        </Button>
      </Flex.Row>
    </Stack>
  );
}

// Fetches the product then renders the form; mounted only while the modal is open.
function DownloadTemplateContents({ openapiProductId, onClose }: { openapiProductId: number; onClose: () => void }) {
  const { classes } = useStyles();
  const productQuery = GraphQL.useOpenApiProductQuery(
    { id: openapiProductId },
    { refetchOnMount: true, retry: shouldRetry(onClose) }
  );
  const openapiProduct = productQuery.data?.openapiProduct;

  if (productQuery.isLoading) {
    return (
      <Flex.Row justify="center" className={classes.loader} data-testid="download-template-loader">
        <Loader color={tokens.color.semantic.icon.primary} size="xl" />
      </Flex.Row>
    );
  }

  if (productQuery.error || !openapiProduct) {
    return (
      <Stack spacing={16} data-testid="download-template-error">
        <Body>Sorry, the template options for this product could not be loaded. Please try again.</Body>
        <Flex.Row justify="flex-end">
          <Button variant="outlined" intent="neutral" onClick={onClose}>
            Close
          </Button>
        </Flex.Row>
      </Stack>
    );
  }

  const parsedAttributes = ProductAttributesSchema.safeParse(openapiProduct.skuAttributes);
  const attributes = parsedAttributes.success ? parsedAttributes.data : {};
  const bounds = getPageCountBounds(openapiProduct.productSpecification?.specification);

  return (
    <DownloadTemplateForm
      manufacturingSku={String(openapiProduct.openapiSku ?? "")}
      attributes={attributes}
      bounds={bounds}
      onClose={onClose}
    />
  );
}

export function DownloadTemplateModal({ opened, openapiProductId, onClose }: DownloadTemplateModalProps) {
  // Keep the last-known product id mounted so the modal's close animation plays over its content
  // instead of an empty body (the parent clears `openapiProductId` synchronously with `opened`).
  // Held during render rather than synced in an effect: an effect would blank the body for one
  // commit on every open, since `displayId` would still be the previous id as the modal appears.
  const [lastKnownId, setLastKnownId] = React.useState<number | undefined>(openapiProductId);
  if (openapiProductId !== undefined && openapiProductId !== lastKnownId) {
    setLastKnownId(openapiProductId);
  }
  const displayId = openapiProductId ?? lastKnownId;

  return (
    <Modal
      open={opened}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <ModalContent size="small" title="Download Template">
        {displayId !== undefined && (
          <DownloadTemplateContents key={displayId} openapiProductId={displayId} onClose={onClose} />
        )}
      </ModalContent>
    </Modal>
  );
}
